import { FileText, Music, Video, Image as ImageIcon, File, MessageSquare, Eye } from 'lucide-react';

const TYPE_CONFIG = {
  audio: { icon: Music, color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20', glow: 'shadow-violet-500/5' },
  video: { icon: Video, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', glow: 'shadow-rose-500/5' },
  image: { icon: ImageIcon, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', glow: 'shadow-emerald-500/5' },
  document: { icon: FileText, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', glow: 'shadow-cyan-500/5' },
};

const DocumentCard = ({ document, backendUrl, onClick, onAnalyze, index = 0 }) => {
  const config = TYPE_CONFIG[document.type] || TYPE_CONFIG.document;
  const Icon = config.icon;
  const fileUrl = `${backendUrl}/api/uploads/${document.file_path}`;

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div
      className="group relative cursor-pointer rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30"
      onClick={onClick}
      style={{ animationDelay: `${Math.min(index * 30, 600)}ms` }}
      data-testid={`document-card-${document.id}`}
    >
      {/* Card body */}
      <div className={`relative bg-slate-900/80 border border-slate-800/60 rounded-lg overflow-hidden group-hover:border-slate-700/80 transition-colors duration-300`}>
        {/* Thumbnail area */}
        <div className="relative h-36 overflow-hidden">
          {document.type === 'image' ? (
            <>
              <img
                src={fileUrl}
                alt={document.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <div className="hidden items-center justify-center w-full h-full bg-gradient-to-b from-emerald-950/40 to-slate-950">
                <Icon className="w-10 h-10 text-slate-600" />
              </div>
            </>
          ) : (
            <div className={`flex items-center justify-center w-full h-full bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950`}>
              <div className={`${config.bg} rounded-2xl p-5 border ${config.border} transition-transform duration-300 group-hover:scale-110`}>
                <Icon className={`w-8 h-8 ${config.color}`} />
              </div>
            </div>
          )}

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full p-2" title="View">
                <Eye className="w-4 h-4 text-white" />
              </div>
              {onAnalyze && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAnalyze(); }}
                  className="bg-cyan-500/20 backdrop-blur-md border border-cyan-400/30 rounded-full p-2 hover:bg-cyan-500/40 transition-colors"
                  title="Analyze in Notebook"
                  data-testid={`analyze-${document.id}`}
                >
                  <MessageSquare className="w-4 h-4 text-cyan-300" />
                </button>
              )}
            </div>
          </div>

          {/* Type badge */}
          <div className="absolute top-2 left-2">
            <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${config.bg} ${config.color} border ${config.border} backdrop-blur-sm`}>
              {document.type}
            </span>
          </div>

          {/* Featured badge */}
          {document.is_featured && (
            <div className="absolute top-2 right-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                Featured
              </span>
            </div>
          )}
          {/* Access tier badge */}
          {!document.is_featured && document.access_tier === 'paid' && (
            <div className="absolute top-2 right-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400/80 border border-amber-500/20 backdrop-blur-sm">
                $5
              </span>
            </div>
          )}
          {!document.is_featured && document.access_tier === 'restricted' && (
            <div className="absolute top-2 right-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400/80 border border-red-500/20 backdrop-blur-sm">
                Restricted
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3">
          <h3
            className="text-sm font-medium text-slate-200 line-clamp-2 leading-snug mb-2 group-hover:text-white transition-colors"
            data-testid={`document-title-${document.id}`}
          >
            {document.title}
          </h3>
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[11px] ${config.color} truncate opacity-70`}>{document.category}</span>
            <span className="text-[11px] text-slate-600 flex-shrink-0">{formatSize(document.file_size)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
